
<template>
    <div class="settings-dashboard">
        <h2 class="settings-heading">
            Settings
        </h2>

        <div class="settings-filter">
            <div class="search">
                <ds-input-field
                    id="search-input"
                    v-model="search"
                    label="Search settings"
                    data-qa="search-results"
                >
                    <template #trailing>
                        <span
                            v-show="search"
                            class="clear-icon"
                            @click="clearSearch"
                        >
                            <DsIcon name="x-circle-fill" />
                        </span>
                    </template>
                </ds-input-field>
            </div>

            <div class="filter-settings">
                <div class="module-name" data-qa="module-name">
                    <ds-chip
                        v-for="(list,index) of displaymenuItems"
                        :key="index"
                        class="menu-chip"
                        remove
                        with-interaction
                        @click.native.stop="handleRemove(index)"
                    >
                        {{ list }}
                    </ds-chip>
                </div>

                <ds-dropdown
                    ref="dropdown"
                    disable-scroll
                    position="bottom-end"
                    data-qa="action-dropdown"
                    :is-open="isActionDropdownOpen"
                    @close="closeActions"
                >
                    <template #default>
                        <ds-text-button
                            class="filter-button"
                            @click="toggleDropdownAction"
                        >
                            <DsIcon name="filter" />
                            <span>Filter</span>
                        </ds-text-button>
                    </template>

                    <template #menu>
                        <ul class="dropdown-menu-list">
                            <li
                                v-for="(item,i) of menuItems"
                                :key="i"
                                :class="['dropdown-menu-item', { selected: isSelectedValues(item)}]"
                                @click="selectedSettings(item)"
                            >
                                {{ item }}
                            </li>
                        </ul>
                    </template>
                </ds-dropdown>
            </div>
        </div>

        <div v-if="!noSearchResults">
            <div v-if="crmView" class="crm-section" data-qa="crm-section">
                <div
                    v-if="searchPersonCompanyResults.length > 0"
                    class="section-header"
                >
                    <p class="section-title">
                        Contacts and Companies
                    </p>
                    <div>
                        <ds-list-item
                            v-for="(settings, index) of searchPersonCompanyResults"
                            :key="index"
                            as="a"
                            :title="settings.title"
                            :description="settings.description"
                            @click="handleUrlClick(settings)"
                        >
                            <template #leading>
                                <DsIcon
                                    :name="settings.icon"
                                />
                            </template>

                            <template #trailing>
                                <DsIcon name="chevron-right" />
                            </template>
                        </ds-list-item>
                    </div>
                </div>

                <div v-if="searchSalesResults.length > 0" class="section-header">
                    <p class="section-title">
                        Sales
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchSalesResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="sales-list"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>

                <div v-if="searchReferralResults.length > 0" class="section-header">
                    <p class="section-title">
                        Referral Partner
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchReferralResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="referralPartner-list"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>
            </div>

            <div v-if="marketingView" class="marketing-section" data-qa="marketing-section">
                <div v-if="searchMarketingResults.length > 0" class="section-header">
                    <p class="section-title">
                        Marketing
                    </p>
                    <template v-for="(settings, index) of searchMarketingResultsFiltered">
                        <ds-list-item
                            :key="index"
                            as="a"
                            :title="settings.title"
                            :description="settings.description"
                            data-qa="marketing-list"
                            :data-module="`${settings.settingModuleName}-${settings.name}`"

                            @click="handleUrlClick(settings)"
                        >
                            <template #leading>
                                <DsIcon
                                    :name="settings.icon"
                                />
                            </template>

                            <template #trailing>
                                <DsIcon name="chevron-right" />
                            </template>
                        </ds-list-item>
                    </template>
                </div>

                <div v-if="searchTemplateResults.length > 0" class="section-header">
                    <p class="section-title">
                        Template
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchTemplateResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="template-list"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>
            </div>

            <div v-if="ecommerceView" class="ecommerce-section" data-qa="ecommerce-section">
                <div v-if="searchOrderResults.length > 0" class="section-header">
                    <p class="section-title">
                        Order
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchOrderResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="ecommerce-list"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>

                <div v-if="searchPaymentResults.length > 0" class="section-header">
                    <p class="section-title">
                        Payment Processing
                    </p>

                    <ds-list-item
                        v-for="(settings, index) of searchPaymentResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        class="keap-merchant-accounts"
                        data-qa="payment-list"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>

                <div v-if="searchFulfillmentListResults.length > 0" class="section-header">
                    <p class="section-title">
                        FulfillmentList
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchFulfillmentListResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="fulfillmentList-list"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>

                <div v-if="searchOrderAutomationListResults.length > 0" class="section-header">
                    <p class="section-title">
                        Order and billing Automation
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchOrderAutomationListResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="orderandBilling-list"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>
            </div>

            <div v-if="adminView" class="admin-section" data-qa="admin-section">
                <div v-if="searchApplicationListResults.length > 0" class="section-header">
                    <p class="section-title">
                        Application
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchApplicationListResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="application-list"
                        :data-module="`${settings.settingModuleName}-${settings.name}`"
                        :data-url="settings.directUrl || settings.externalUrl"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template v-if="settings.feature" #title>
                            {{ settings.title }}
                            <span class="feature-label">{{ settings.feature }}</span>
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>

                <div v-if="searchCollaborationListResults.length > 0" class="section-header">
                    <p class="section-title">
                        Collaboration
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchCollaborationListResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="collaboration-list"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>

                <div v-if="searchUserListResults.length > 0" class="section-header">
                    <p class="section-title">
                        User
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchUserListResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="user-list"
                        :data-module="`${settings.name}`"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>

                <div v-if="searchStealthListResults.length > 0" class="section-header">
                    <p class="section-title">
                        Stealth
                    </p>
                    <ds-list-item
                        v-for="(settings, index) of searchStealthListResults"
                        :key="index"
                        as="a"
                        :title="settings.title"
                        :description="settings.description"
                        data-qa="stealth-list"
                        @click="handleUrlClick(settings)"
                    >
                        <template #leading>
                            <DsIcon
                                :name="settings.icon"
                            />
                        </template>

                        <template #trailing>
                            <DsIcon name="chevron-right" />
                        </template>
                    </ds-list-item>
                </div>
            </div>
        </div>
        <div v-else>
            <h4>No results found for {{ search }}</h4>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {

    props: {
        systemId: {
            type: String,
            default: '',
        },

        isSimplifiedAffiliateModuleEnabled: {
            type: String,
            default: '',
        },

        isRolesPermissionsEnabled: {
            type: String,
            default: '',
        },
        isBrandingCenterEnabled: {
            type: Boolean,
        },
    },

    data() {
        return {
            search: '',
            allSettings: [],
            personCompanyList: [],
            salesList: [],
            affiliateList: [],
            marketingList: [],
            templateList: [],
            orderList: [],
            paymentList: [],
            fulfillmentList: [],
            orderAutomationList: [],
            applicationList: [],
            collaborationList: [],
            userList: [],
            stealthList: [],
            isActionDropdownOpen: false,
            menuItems: ['CRM', 'Marketing', 'E-Commerce', 'Admin'],
            displaymenuItems: [],
            crmView: true,
            marketingView: true,
            ecommerceView: true,
            adminView: true,
        };
    },

    created() {
        if (this.systemId) {
            localStorage.removeItem('filterList');
            localStorage.setItem('systemId', this.systemId);
            this.load();
        } else {
            this.load();

            if (localStorage.getItem('filterList')) {
                if (JSON.parse(localStorage.getItem('filterList')).length >= 1) {
                    this.loadFilterSettings();
                }
            }
        }
    },


    computed: {

        noSearchResults() {
            return this.search && !this.searchPersonCompanyResults?.length && !this.searchSalesResults?.length && !this.searchReferralResults?.length
            && !this.searchMarketingResults?.length && !this.searchOrderResults?.length && !this.searchPaymentResults?.length && !this.searchTemplateResults?.length
            && !this.searchFulfillmentListResults?.length && !this.searchOrderAutomationListResults?.length && !this.searchApplicationListResults?.length
            && !this.searchCollaborationListResults?.length && !this.searchUserListResults?.length && !this.searchStealthListResults?.length;
        },

        searchCheck() {
            return (searchData) => {
                if (this.search) {
                    return searchData.filter((label) => label.title.toLowerCase().includes(this.search.toLowerCase()) || label.description.toLowerCase().includes(this.search.toLowerCase()));
                }

                return searchData;
            };
        },

        searchPersonCompanyResults() {
            const aliasPersonCompanyList = this.personCompanyList && this.personCompanyList.length > 0 ? this.personCompanyList.map((x) => x) : [];

            return this.searchCheck(aliasPersonCompanyList);
        },

        searchSalesResults() {
            const aliasSalesList = this.salesList && this.salesList.length > 0 ? this.salesList.map((x) => x) : [];

            return this.searchCheck(aliasSalesList);
        },

        searchReferralResults() {
            const aliasAffiliateList = this.affiliateList && this.affiliateList.length > 0 ? this.affiliateList.map((x) => x) : [];

            return this.searchCheck(aliasAffiliateList);
        },

        searchMarketingResults() {
            const aliasMarketingList = this.marketingList && this.marketingList.length > 0 ? this.marketingList.map((x) => x) : [];

            return this.searchCheck(aliasMarketingList);
        },

        searchTemplateResults() {
            const aliasTemplateList = this.templateList && this.templateList.length > 0 ? this.templateList.map((x) => x) : [];

            return this.searchCheck(aliasTemplateList);
        },

        searchOrderResults() {
            const aliasOrderList = this.orderList && this.orderList.length > 0 ? this.orderList.map((x) => x) : [];

            return this.searchCheck(aliasOrderList);
        },

        searchPaymentResults() {
            const aliasPaymentList = this.paymentList && this.paymentList.length > 0 ? this.paymentList.map((x) => x) : [];

            return this.searchCheck(aliasPaymentList);
        },

        searchFulfillmentListResults() {
            const aliasFulfillmentList = this.fulfillmentList && this.fulfillmentList.length > 0 ? this.fulfillmentList.map((x) => x) : [];

            return this.searchCheck(aliasFulfillmentList);
        },

        searchOrderAutomationListResults() {
            const aliasOrderAutomationList = this.orderAutomationList && this.orderAutomationList.length > 0 ? this.orderAutomationList.map((x) => x) : [];

            return this.searchCheck(aliasOrderAutomationList);
        },

        searchApplicationListResults() {
            const aliasApplicationList = this.applicationList && this.applicationList.length > 0 ? this.applicationList.map((x) => x) : [];

            return this.searchCheck(aliasApplicationList);
        },

        searchCollaborationListResults() {
            const aliasCollaborationList = this.collaborationList && this.collaborationList.length > 0 ? this.collaborationList.map((x) => x) : [];

            return this.searchCheck(aliasCollaborationList);
        },

        searchUserListResults() {
            const aliasUserList = this.userList && this.userList.length > 0 ? this.userList.map((x) => x) : [];

            return this.searchCheck(aliasUserList);
        },

        searchStealthListResults() {
            const aliasStealthList = this.stealthList && this.stealthList.length > 0 ? this.stealthList.map((x) => x) : [];

            return this.searchCheck(aliasStealthList);
        },

        searchMarketingResultsFiltered() {
            return this.isBrandingCenterEnabled ? this.searchMarketingResults : this.searchMarketingResults.filter((setting) => {
                return setting.title !== 'Branding Center';
            });
        },
    },

    methods: {

        async load() {
            window.history.pushState(null, null, '/app/miscSetting/showSection');
            this.loadAllSettings();

            this.loadMenu();
        },

        clearSearch() {
            this.search = '';
        },

        async loadAllSettings() {
            try {
                const response = await axios.get('/app/miscSetting/settings');

                this.allSettings = response.data;
                this.allSettings.forEach((settings) => {
                    this.GroupingSettingSystem(settings);
                });
            } catch (e) {
                this.$error({ message: 'Unable get all settings' });
            }
        },


        GroupingSettingSystem(settings) {
            switch (settings.settingModuleName) {
            case 'PersonCompany':
                this.personCompanyList.push(settings);
                break;
            case 'Sales':
                this.salesList.push(settings);
                break;
            case 'Affiliate':
                this.allAffiliates(settings);
                break;
            case 'Marketing':
                this.marketingList.push(settings);
                break;
            case 'Template':
                this.templateList.push(settings);
                break;
            case 'Order':
                this.orderList.push(settings);
                break;
            case 'Payment':
                this.paymentList.push(settings);
                break;
            case 'Fulfillment':
                this.fulfillmentList.push(settings);
                break;
            case 'OrderAutomation':
                this.orderAutomationList.push(settings);
                break;
            case 'Application':
                this.applicationList.push(settings);
                break;
            case 'Collaboration':
                this.collaborationList.push(settings);
                break;
            case 'User':
                this.allUsers(settings);
                break;
            case 'Stealth':
                this.stealthList.push(settings);
                break;
            default:
                break;
            }
        },

        toggleDropdownAction() {
            this.isActionDropdownOpen = !this.isActionDropdownOpen;
        },

        closeActions() {
            this.isActionDropdownOpen = false;
        },

        isSelectedValues(val) {
            return this.displaymenuItems.find((item) => item === val);
        },

        loadMenu() {
            const systemId = localStorage.getItem('systemId');

            switch (systemId) {
            case 'nav.crm':
                this.displaymenuItems.push('CRM');
                this.crmView = true;
                this.marketingView = false;
                this.ecommerceView = false;
                this.adminView = false;
                break;
            case 'nav.marketing':
                this.displaymenuItems.push('Marketing');
                this.crmView = false;
                this.marketingView = true;
                this.ecommerceView = false;
                this.adminView = false;
                break;
            case 'nav.accounting':
                this.crmView = false;
                this.marketingView = false;
                this.ecommerceView = true;
                this.adminView = false;
                this.displaymenuItems.push('E-Commerce');
                break;
            case 'nav.admin':
                this.crmView = false;
                this.marketingView = false;
                this.ecommerceView = false;
                this.adminView = true;
                this.displaymenuItems.push('Admin');
                break;
            default:
                break;
            }
        },

        allAffiliates(settings) {
            if (JSON.parse(this.isSimplifiedAffiliateModuleEnabled) && settings.name === 'affiliate.affiliate-center') {
                return;
            }
            this.affiliateList.push(settings);
        },

        allUsers(settings) {
            if (JSON.parse(this.isRolesPermissionsEnabled) && settings.name === 'User permissions') {
                return;
            }

            if (!JSON.parse(this.isRolesPermissionsEnabled) && settings.name === 'Users') {
                settings.title = 'Users';
                settings.description = 'View and manage users and partners';
            }

            this.userList.push(settings);
        },

        selectedSettings(value) {
            if (!this.displaymenuItems.includes(value)) {
                this.displaymenuItems.push(value);
            }
            this.crmView = false;
            this.marketingView = false;
            this.ecommerceView = false;
            this.adminView = false;

            localStorage.setItem('filterList', JSON.stringify(this.displaymenuItems));

            this.displaymenuItems.includes('CRM') ? this.crmView = true : '';
            this.displaymenuItems.includes('Marketing') ? this.marketingView = true : '';
            this.displaymenuItems.includes('E-Commerce') ? this.ecommerceView = true : '';
            this.displaymenuItems.includes('Admin') ? this.adminView = true : '';
        },

        handleRemove(id) {
            this.displaymenuItems?.splice(id, 1);

            localStorage.removeItem('filterList');
            localStorage.setItem('filterList', JSON.stringify(this.displaymenuItems));

            (!this.displaymenuItems.includes('CRM')) ? this.crmView = false : '';
            (!this.displaymenuItems.includes('Marketing')) ? this.marketingView = false : '';
            (!this.displaymenuItems.includes('E-Commerce')) ? this.ecommerceView = false : '';
            (!this.displaymenuItems.includes('Admin')) ? this.adminView = false : '';

            if (this.displaymenuItems.length === 0) {
                this.crmView = true;
                this.marketingView = true;
                this.ecommerceView = true;
                this.adminView = true;
                localStorage.removeItem('systemId');
            }
        },

        handleUrlClick(settings) {
            if (settings.directUrl) {
                window.open(settings.directUrl, '_self');
            } else if (settings.externalUrl) {
                window.open(settings.externalUrl, '_blank');
            }
        },

        loadFilterSettings() {
            this.displaymenuItems = JSON.parse(localStorage.getItem('filterList'));
            this.displaymenuItems.includes('CRM') ? this.crmView = true : this.crmView = false;
            this.displaymenuItems.includes('Marketing') ? this.marketingView = true : this.marketingView = false;
            this.displaymenuItems.includes('E-Commerce') ? this.ecommerceView = true : this.ecommerceView = false;
            this.displaymenuItems.includes('Admin') ? this.adminView = true : this.adminView = false;
        },

    },

};
</script>

<style lang="scss" scoped>
.settings-filter {
    display: flex;
    padding-top: $spacing-250;
    justify-content: space-between;
}

.clear-icon {
    --icon-size: #{$icon-size-small};
    --icon-color: gray;
    cursor: pointer;
}

.search {
    display: flex;
    justify-content: space-between;
}

.filter-button {
    justify-content: space-around;
    display: flex;

    span {
        margin-left: $spacing-100;
        padding-top: $spacing-025;
        font-size: $font-size-sm;
    }

    .icon {
        height: $icon-size-sm;
    }
}

.filter-settings {
    display: flex;
}

.section-header {
    font-size: $font-size-md;
    padding-top: $spacing-200;
    justify-content: center;

    .section-title {
        font-weight: $font-weight-bold;
    }
}

.module-name {
    margin-right: $spacing-100;
    display: flex;
    margin-bottom: $spacing-100;;
    align-items: center;

    .menu-chip {
         margin-left : $spacing-100;
    }
}

.feature-label {
    color: $color-paper;
    background-color: $color-blue;
    padding: px-to-rem(2px) px-to-rem(5px);
    border-radius: px-to-rem(3px);
    font-size: $font-size-xs;
    font-weight: $font-weight-semibold;
}
</style>
